// Customer-facing tax treatment of a single catalog item, as the API resolves it
// (packages/api/src/modules/payment/billing.calc.ts -> CheckoutTaxTreatment). It is declared here
// because mobile and web both render it and neither can import from packages/api.
export type SingleTaxTreatment =
    | { kind: 'vat'; rate: number }
    | { kind: 'exempt'; exemptType: 'objective' | 'subjective' }
    | { kind: 'margin' }

// A basket whose lines do not all share one treatment: each part carries its own treatment and
// the gross it applies to, mirroring the itemised invoice such a basket produces. Only a
// transaction can be mixed — a single catalog item always resolves to one treatment.
export type MixedTaxTreatment = {
    kind: 'mixed'
    parts: { treatment: SingleTaxTreatment; gross: number }[]
}

export type TaxTreatment = SingleTaxTreatment | MixedTaxTreatment

export type VatBreakdown = {
    gross: number
    net: number
    vatAmount: number
    rate: number | null
    hasVatSplit: boolean
}

// Mirrors buildVatLiableLineItems: the net is rounded and the VAT is the remainder, never rounded
// on its own, so the three numbers on screen always add back up to the amount actually charged and
// match the issued invoice.
//
// Exempt and margin items get no split. A margin item in particular is invoiced with a net of
// totalGross - marginVat (buildMarginSchemeLineItem), not gross / 1.27 — on an 85 000 Ft ticket at
// a 20% commission rate the invoice reads 81 386 / 3 614, where a 27% split would show 66 929 /
// 18 071. Splitting here would put numbers on screen that cannot be reconciled with the invoice the
// customer receives, so margin shows the gross and says so. This is also what web does:
// priceFormatterFor quotes anything whose kind is not 'vat' at its gross figure.
export const calculateVatBreakdown = (
    gross: number,
    tax: TaxTreatment | null | undefined,
): VatBreakdown => {
    if (tax?.kind === 'mixed') {
        return calculateMixedVatBreakdown(gross, tax)
    }

    if (tax?.kind !== 'vat') {
        return { gross, net: gross, vatAmount: 0, rate: null, hasVatSplit: false }
    }

    const net = Math.round(gross / (1 + tax.rate / 100))

    return { gross, net, vatAmount: gross - net, rate: tax.rate, hasVatSplit: true }
}

// A mixed basket has no single rate to name, so `rate` stays null and only the VAT actually
// charged is summed: each part is split at its own rate, and an exempt or margin part
// contributes nothing. The caller passes the basket total, which the parts are expected to add
// up to; the returned gross is the total, so the three numbers still reconcile on screen.
const calculateMixedVatBreakdown = (gross: number, tax: MixedTaxTreatment): VatBreakdown => {
    const vatAmount = tax.parts.reduce(
        (total, part) => total + calculateVatBreakdown(part.gross, part.treatment).vatAmount,
        0,
    )

    return {
        gross,
        net: gross - vatAmount,
        vatAmount,
        rate: null,
        hasVatSplit: vatAmount > 0,
    }
}
